import { Alert, Box, Button, CircularProgress, LinearProgress, Link, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DateTimePicker } from "@mui/x-date-pickers";
import { Item } from "./Item";
import { MyListApi } from "../utils/restApi";
import { clearShareExperienceItem, setShareExperienceItem } from "../slices/shareExperienceSlice";
import { UploadImage } from "./UploadImage";
import { addNotification } from "../slices/notificationSlice";
import { formatLabel, hasUserDoneAction } from "../utils/itemUtil";

// Dedicated, item-scoped page for sharing an experience. Reached from an
// item as /share-experience/:itemId (the action is chosen here) or through
// existing deep links that preselect one: /share-experience/:itemId/:action.
export const ShareExperience = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { itemId, action } = useParams();
    const user = useSelector(state => state.login.user);
    const storedItem = useSelector(state => state.shareExperience.item);
    const item = storedItem && String(storedItem.id) === String(itemId) ? storedItem : null;
    const [selectedAction, setSelectedAction] = useState('');
    const [onDate, setOnDate] = useState(new Date());
    const [imageFilename, setImageFilename] = useState();
    const [loadError, setLoadError] = useState(null);
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (!item && itemId) {
            setLoadError(null);
            MyListApi.getItem(itemId)
                .then(data => {
                    if (!cancelled) dispatch(setShareExperienceItem(data));
                })
                .catch(error => {
                    if (!cancelled) setLoadError(error?.message || 'This item could not be loaded.');
                });
        }
        return () => { cancelled = true; };
    }, [item, itemId, dispatch]);

    const actions = (item?.tag?.actions || []).map(a => ({ name: a.name, isDisabled: hasUserDoneAction(item, a.name) }));
    const requested = action ? actions.find(a => a.name === action) : null;
    const requestedProblem = !item || !action ? null :
        !requested ? `"${formatLabel(action)}" isn't an experience for this item. Choose one below.` :
        requested.isDisabled ? `You've already shared "${formatLabel(action)}". Choose another experience below.` :
        null;

    // Preselect a valid, not-yet-performed action from a deep link.
    const preselect = requested && !requested.isDisabled ? requested.name : '';
    useEffect(() => {
        if (preselect) {
            setSelectedAction(preselect);
        }
    }, [item?.id, preselect]);

    if (!itemId) {
        return <Alert severity="error">No item was selected.</Alert>;
    }

    if (loadError) {
        return <Alert severity="error">{loadError}</Alert>;
    }

    if (!item) {
        return <LinearProgress aria-label="Loading item" />;
    }

    const itemPath = `/items/${item.id}`;
    const allDone = actions.length > 0 && actions.every(a => a.isDisabled);
    const canPost = Boolean(user) && Boolean(selectedAction) && actions.some(a => a.name === selectedAction && !a.isDisabled) && !posting;

    const handleUploadImage = response => {
        setImageFilename(response.filename);
    }

    const handlePost = e => {
        e.preventDefault();
        if (!canPost) return;
        setPosting(true);
        MyListApi.collectItem(item.id, selectedAction, onDate, imageFilename)
            .then(_ => {
                dispatch(clearShareExperienceItem());
                navigate(itemPath);
            })
            .catch(error => {
                setPosting(false);
                dispatch(addNotification({
                    message: error.message,
                    type: 'error',
                }));
            });
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <Box>
                <Link component={RouterLink} to={itemPath} sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem' }}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} aria-hidden="true" />Back to item
                </Link>
            </Box>
            <Box>
                <Typography component="h1" variant="h1">Share your experience</Typography>
                <Typography color="text.secondary" sx={{ mt: '6px' }}>Tell people what you did with this.</Typography>
            </Box>

            <Item item={item} showStats={false} titleComponent="h2" />

            {!user ?
                <Alert severity="info" action={<Button component={RouterLink} to="/login" color="inherit" size="small">Log in</Button>}>
                    Log in to share your experience.
                </Alert>
            : null}
            {requestedProblem ? <Alert severity="info">{requestedProblem}</Alert> : null}
            {allDone ? <Alert severity="success">You've shared every experience available for this item.</Alert> : null}
            {actions.length === 0 ? <Alert severity="info">This category doesn't have any experiences to share yet.</Alert> : null}

            <Paper component="form" variant="outlined" onSubmit={handlePost} sx={{ borderRadius: '14px', p: { xs: '17px', sm: '24px' }, display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <TextField
                    select
                    label="I…"
                    value={selectedAction}
                    onChange={e => setSelectedAction(e.target.value)}
                    disabled={!user || actions.length === 0}
                    required
                    fullWidth
                    helperText="Experiences you've already shared can't be shared again."
                    SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                >
                    <MenuItem value="" disabled><em>Choose an experience</em></MenuItem>
                    {actions.map(a => (
                        <MenuItem key={a.name} value={a.name} disabled={a.isDisabled}>
                            {formatLabel(a.name)}{a.isDisabled ? ' · already shared' : ''}
                        </MenuItem>
                    ))}
                </TextField>
                <DateTimePicker
                    label="When"
                    value={onDate}
                    onChange={d => setOnDate(d)}
                    disableFuture
                    disabled={!user}
                    slotProps={{ textField: { fullWidth: true } }}
                />
                {user ? <UploadImage label="Add photo" height="280px" onChange={handleUploadImage} /> : null}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '10px' }}>
                    <Button component={RouterLink} to={itemPath}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={!canPost} sx={{ minWidth: 120 }}>
                        {posting ? <CircularProgress size={20} color="inherit" aria-label="Posting" /> : 'Post'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
